// Global `v-drag-dialog` directive.
//
// Makes a floating dialog `q-card` draggable by its `navigate-back` header
// (the `.custom-fixed-top` element). Applied on the root `q-card` of the edit
// dialogs. Disabled when the binding value is falsy (e.g. on mobile) and
// auto-disabled while the dialog is maximized (full-screen), where moving it
// would make no sense. Quasar's QDialog has no native drag support, so this is
// the single shared place holding the behaviour.

const HANDLE_SELECTOR = '.custom-fixed-top'
const MAXIMIZED_SELECTOR = '.q-dialog__inner--maximized'
// Elements that own their own click behaviour (back arrow, action buttons):
// a mousedown on them must not start a drag.
const INTERACTIVE_SELECTOR = '.q-btn, .q-icon, button, a, input, textarea, select, [role="button"]'

const applyTransform = (el, state) => {
  el.style.transform = `translate(${state.x}px, ${state.y}px)`
}

const setupHandle = (el, state) => {
  const handle = el.querySelector(HANDLE_SELECTOR)
  if (!handle || state.handle === handle) return
  state.handle = handle
  handle.style.cursor = 'move'
  handle.addEventListener('mousedown', state.onMouseDown)
}

const createState = (el) => {
  const state = { x: 0, y: 0, startX: 0, startY: 0, handle: null, enabled: false }

  const onMouseMove = (e) => {
    state.x = e.clientX - state.startX
    state.y = e.clientY - state.startY
    applyTransform(el, state)
  }

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.body.style.userSelect = ''
  }

  state.onMouseDown = (e) => {
    if (!state.enabled || e.button !== 0) return
    // Full-screen dialog: nothing to drag.
    if (el.closest(MAXIMIZED_SELECTOR)) return
    // Don't hijack clicks meant for the header's own controls.
    if (e.target.closest(INTERACTIVE_SELECTOR)) return
    state.startX = e.clientX - state.x
    state.startY = e.clientY - state.y
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  state.teardown = () => {
    if (state.handle) state.handle.removeEventListener('mousedown', state.onMouseDown)
    onMouseUp()
  }

  return state
}

const dragDialog = {
  mounted(el, binding) {
    const state = createState(el)
    state.enabled = binding.value !== false
    el.__dragDialog = state
    if (state.enabled) setupHandle(el, state)
  },
  updated(el, binding) {
    const state = el.__dragDialog
    if (!state) return
    state.enabled = binding.value !== false
    if (state.enabled) setupHandle(el, state)
  },
  unmounted(el) {
    if (el.__dragDialog) {
      el.__dragDialog.teardown()
      delete el.__dragDialog
    }
  },
}

export default {
  install(app) {
    app.directive('drag-dialog', dragDialog)
  },
}
